import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { AxiosResponse } from 'axios';
import { put, takeLatest } from 'redux-saga/effects';
import { handleError } from 'redux/saga/utils/errorUtils';
import {
  downloadSampleDocument,
  downloadSampleDocumentFailure,
  downloadSampleDocumentSuccess,
  editDocumentDueDate,
  editDocumentDueDateFailure,
  editDocumentDueDateSuccess,
  editDocumentStatus,
  editDocumentStatusFailure,
  editDocumentStatusSuccess,
  fetchAgencyDetail,
  fetchAgencyDetailFailure,
  fetchAgencyDetailSuccess,
  fetchDocuments,
  fetchDocumentsFailure,
  fetchDocumentsSuccess,
  fetchFolders,
  fetchFoldersFailure,
  fetchFoldersSuccess,
  showDocument,
  showDocumentFailure,
  showDocumentSuccess,
  uploadDocument,
  uploadDocumentFailure,
  uploadDocumentSuccess,
} from 'redux/slice/compliance/document/Document.slice';
import {
  IDownloadSampleDocumentRequest,
  IDownloadSampleDocumentResponse,
  IEditDocumentDueDateRequest,
  IEditDocumentResponse,
  IEditDocumentStatusRequest,
  IGetAgencyDetailRequest,
  IGetAgencyDetailResponse,
  IGetDocumentsRequest,
  IGetDocumentsResponse,
  IGetFoldersRequest,
  IGetFoldersResponse,
  IShowDocumentRequest,
  IShowDocumentResponse,
  IUploadDocumentRequest,
  IUploadDocumentResponse,
} from 'redux/types/compliance/Document.types';
import { complianceEndPoints } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

function* handleFetchDocumentsSaga(
  action: PayloadAction<IGetDocumentsRequest>
) {
  try {
    const { filter, agencyId, ...requestBody } = action.payload;

    const endpoint = complianceEndPoints.GET_AGENCY_DOCUMENTS(filter, agencyId);

    const response: AxiosResponse<IGetDocumentsResponse> =
      yield HTTPClient.PostRequest(endpoint, requestBody);

    yield put(fetchDocumentsSuccess(response.data));
    message.success(response.data.message);
  } catch (error: any) {
    yield* handleError(error, fetchDocumentsFailure, '');
  }
}

function* handleUploadDocumentSaga(
  action: PayloadAction<IUploadDocumentRequest>
) {
  try {
    const { agencyId, documentColumnMasterId, file, entityDocumentId } =
      action.payload;
    const endpoint = complianceEndPoints.UPLOAD_DOCUMENT;

    const formdata = new FormData();
    formdata.append('agencyId', agencyId);
    formdata.append('documentColumnMasterId', documentColumnMasterId);
    formdata.append('file', file);
    formdata.append('entityDocumentId', entityDocumentId);

    const response: AxiosResponse<IUploadDocumentResponse> =
      yield HTTPClient.PostRequest(endpoint, formdata, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    yield put(uploadDocumentSuccess(response.data));

    // Call show document action
    const handleShowDocumentAction = {
      type: showDocument.type,
      payload: {
        entityDocumentId: response.data.data.entityDocumentId,
      },
    };
    yield put(handleShowDocumentAction); // Dispatch the action
    message.success(response.data.message);
  } catch (error: any) {
    yield* handleError(error, uploadDocumentFailure, '');
  }
}

function* handleShowDocumentSaga(action: PayloadAction<IShowDocumentRequest>) {
  try {
    const { entityDocumentId } = action.payload;

    const endpoint = complianceEndPoints.SHOW_DOCUMENT(entityDocumentId);

    const response: AxiosResponse<IShowDocumentResponse> =
      yield HTTPClient.GetRequest(endpoint);

    yield put(showDocumentSuccess(response.data));
    message.success(response.data.message);
  } catch (error: any) {
    yield* handleError(error, showDocumentFailure, '');
  }
}

function* handleDownloadSampleDocumentSaga(
  action: PayloadAction<IDownloadSampleDocumentRequest>
) {
  try {
    const { documentMasterId } = action.payload;

    const endpoint =
      complianceEndPoints.DOWNLOAD_SAMPLE_DOCUMENT(documentMasterId);

    const response: AxiosResponse<IDownloadSampleDocumentResponse> =
      yield HTTPClient.GetRequest(endpoint);

    yield put(downloadSampleDocumentSuccess(response.data));
    window.open(response.data.data.url, '_blank');
    message.success(response.data.message);
  } catch (error: any) {
    yield* handleError(error, downloadSampleDocumentFailure, '');
  }
}

function* handleFetchAgencyDetailSaga(
  action: PayloadAction<IGetAgencyDetailRequest>
) {
  try {
    const { agencyId } = action.payload;

    const endpoint = complianceEndPoints.GET_AGENCY_DETAIL(agencyId);

    const response: AxiosResponse<IGetAgencyDetailResponse> =
      yield HTTPClient.GetRequest(endpoint);

    yield put(fetchAgencyDetailSuccess(response.data));
    message.success(response.data.message);
  } catch (error: any) {
    yield* handleError(error, fetchAgencyDetailFailure, '');
  }
}

function* handleEditDocumentStatusSaga(
  action: PayloadAction<IEditDocumentStatusRequest>
) {
  try {
    const { documentId, status, agencyId, comment, loginId } = action.payload;

    const endpoint = complianceEndPoints.EDIT_DOCUMENT_STATUS();

    const response: AxiosResponse<IEditDocumentResponse> =
      yield HTTPClient.PostRequest(endpoint, {
        documentId,
        status,
        agencyId,
        comment,
        loginId,
      });

    yield put(editDocumentStatusSuccess(response.data));
    message.success(response.data.message);
  } catch (error: any) {
    yield* handleError(error, editDocumentStatusFailure, '');
  }
}

function* handleEditDocumentDueDateSaga(
  action: PayloadAction<IEditDocumentDueDateRequest>
) {
  try {
    const { documentId, dueDate, agencyId, loginId } = action.payload;

    const endpoint = complianceEndPoints.EDIT_DOCUMENT_DUE_DATE();

    const response: AxiosResponse<IEditDocumentResponse> =
      yield HTTPClient.PostRequest(endpoint, {
        documentId,
        dueDate,
        agencyId,
        loginId,
      });

    yield put(editDocumentDueDateSuccess(response.data));
    message.success(response.data.message);
  } catch (error: any) {
    yield* handleError(error, editDocumentDueDateFailure, '');
  }
}

function* handleFetchFoldersSaga(action: PayloadAction<IGetFoldersRequest>) {
  try {
    const { agencyId, allocatorId, createdBy, folderName } = action.payload;

    const endpoint = complianceEndPoints.GET_FOLDERS(agencyId, allocatorId);

    const response: AxiosResponse<IGetFoldersResponse> =
      yield HTTPClient.PostRequest(endpoint, {
        agencyId,
        allocatorId,
      });

    yield put(fetchFoldersSuccess(response.data));
    message.success(response.data.message);
  } catch (error: any) {
    yield* handleError(error, fetchFoldersFailure, '');
  }
}
export function* watchDocumentSaga() {
  yield takeLatest(fetchDocuments.type, handleFetchDocumentsSaga);
  yield takeLatest(uploadDocument.type, handleUploadDocumentSaga);
  yield takeLatest(showDocument.type, handleShowDocumentSaga);
  yield takeLatest(
    downloadSampleDocument.type,
    handleDownloadSampleDocumentSaga
  );
  yield takeLatest(fetchAgencyDetail.type, handleFetchAgencyDetailSaga);
  yield takeLatest(editDocumentStatus.type, handleEditDocumentStatusSaga);
  yield takeLatest(editDocumentDueDate.type, handleEditDocumentDueDateSaga);
  yield takeLatest(fetchFolders.type, handleFetchFoldersSaga);
}
